import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { convertXcaliberEntitiesToCMX } from '../xcaliber/entity-converter';
import { sequenceGenerator } from '../sequence-generator';
import * as invoiceUtils from '../utils/invoice-utilities';
import * as api from '../../api/api';
import * as appServiceLines from '../../service-lines';
import * as cmxDateTime from '../../date-time/cmx-date-time';
import * as codeBenchActions from '../../../actions/code-bench.actions';
import HttpMethods from '../../../enums/http-methods';
import * as uiActions from '../../../actions/ui.actions';
import { InvoiceEditor } from '../../../actions/action-types';
import { FeedbackMeta } from '../../../enums';
import { fetchAllInvoices, fetchActivity } from '../../../actions/code-bench.actions';
import { fetchXcaliberToken } from '../middlewares/xcaliber.middleware';
import { convertXcaliberEntitiesToIPC } from '../xcaliber/entity-converter';
import { ProviderType } from '../../../enums';
import { capabilityValue } from '../utils/conditional-utilities';
import { buildDataAccessRequestObject, shouldFetchFromDataAccess } from '../../artifacts/utils';

interface ErrorWithFieldErrors {
  errors: {
    fieldErrors: Record<string, string>;
  };
}

const { amplifyFeedback } = uiActions;

const REQUEST_INIT_OPTIONS: RequestInit = {
  method: 'GET',
  cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
  redirect: 'follow',
  referrer: 'no-referrer'
};
const CONSTELLATION_SERVICES_LOCATION = '/constellation';

const cancelChargeEdit = createAction<void>(InvoiceEditor.CANCEL_INVOICE);
const removeProvider = createAction<void>(InvoiceEditor.REMOVE_PROVIDER);
const addProvider = createAction<CMxAPI.Provider>(InvoiceEditor.ADD_PROVIDER);
const billingAreaOptions = createAction<{ label: string; value: string }[]>(
  InvoiceEditor.BILLING_AREA_OPTIONS
);

// Action initializers
const removeAdditionPerformer = createAction<void>(
  InvoiceEditor.REMOVE_PERFORMER
);
const removeAdditionReferringPerformer = createAction<void>(
  InvoiceEditor.REMOVE_REFERRING_PERFORMER
);
const addAdditionPerformer = createAction<CMxAPI.Provider>(
  InvoiceEditor.ADD_PERFORMER
);
const addAdditionReferringPerformer = createAction<CMxAPI.Provider>(
  InvoiceEditor.ADD_REFERRING_PERFORMER
);
const changePerformDate = createAction<string>(
  InvoiceEditor.CHANGE_DATE_PERFORMER
);

const departmentChange = createAction<string>(InvoiceEditor.DEPARTMENT_CHANGE);

const feeScheduleChange = createAction<string>(
  InvoiceEditor.FEE_SCHEDULE_CHANGE
);

// Action for choose a performer
const chooseAdditionPerformer = createAsyncThunk<
  CMxAPI.Provider,
  CMxAPI.Provider,
  { rejectValue: any }
>(InvoiceEditor.CHOOSE_PERFORMER, async (provider, { dispatch }) => {
  return dispatch(addAdditionPerformer(provider)) as any;
});

// Action for choose a referring performer
const chooseAdditionReferringPerformer = createAsyncThunk<
  void,
  CMxAPI.Provider,
  { rejectValue: any }
>(InvoiceEditor.CHOOSE_REFERRING_PERFORMER, async (provider, { dispatch }) => {
  dispatch(addAdditionReferringPerformer(provider));
});

type ProviderResults = {
  providers: CMxAPI.Provider[];
  meta: {
    searchType: string;
    providerType: string;
    sequenceNumber: number;
  };
};

const getProviders = async (lookupQuery: CMxCommonApp.LookupQuery) => {
  const { context } = lookupQuery;
  const { organization } = context;
  const tenantId = organization.tenantId ?? '';

  const lookupText = (lookupQuery.text ?? '').trim();

  if (lookupText.length) {
    const providerQuery =
      lookupQuery.searchType === 'id'
        ? { idNumber: lookupText, searchType: 'ID', tenantId }
        : { lastName: `${lookupText}*`, searchType: 'NAME', tenantId };

    return await api.requestJSON<CMxAPI.Provider[]>(`/provider/find/v1`, {
      ...REQUEST_INIT_OPTIONS,
      method: HttpMethods.POST,
      body: JSON.stringify(providerQuery)
    });
  }
  return [];
};

const buildXCaliberQuery = (
  lookupQuery: CMxCommonApp.LookupQuery,
  limit: number = 25
) => {
  const lookupText = (lookupQuery.text ?? '').trim();
  const query = ['select * from provider where'];

  if (lookupQuery.searchType === 'id') {
    query.push(`provider.prov_id = '${lookupText}'`);
  } else {
    query.push(`prov_name like '%${lookupText}%'`);
  }

  query.push(`limit ${limit}`);
  query.push(`;`);

  return { query: query.join(' ') };
};


const providerSearchXCaliber = createAsyncThunk<
  ProviderResults,
  CMxCommonApp.LookupQuery,
  { rejectValue: any }
>(InvoiceEditor.SEARCH_PROVIDERS_XCALIBER, async (lookupQuery, { getState }) => {
  const {
    context: { searchType, meetsMinimum, processInstance, providerType }
  } = lookupQuery;

  const sequenceNumber = sequenceGenerator.next(searchType);
  const isIPCServiceLine = appServiceLines.isIPC(processInstance?.serviceLine) ?? false;
  const isSurgery = appServiceLines.isSurgery(processInstance?.serviceLine) ?? false;

  const state = getState();

  let providers: any[] = [];

  if (meetsMinimum) {
    const isPathology = appServiceLines.isPathology(processInstance?.serviceLine) ?? false;
    if (isIPCServiceLine || isSurgery) {
      providers = await getProviderFromDirectoryV2XCaliber(lookupQuery, state);
    } else {
      providers = await getProviderFromDirectoryXCaliber(
        lookupQuery,
        state,
        false,
        isPathology
      );
    }
  }

  return {
    providers: invoiceUtils.providerOrder(providers),
    meta: {
      searchType,
      providerType,
      sequenceNumber
    }
  };
});

const getProviderFromDirectoryXCaliber = async (
  lookupQuery: CMxCommonApp.LookupQuery,
  state: any,
  isIPCServiceLine: boolean,
  isPathology: boolean
) => {
  const lookupText = (lookupQuery.text ?? '').trim();
  if (!lookupText.length) return [];

  const providerQuery = buildXCaliberQuery(lookupQuery);
  const xcaliberToken = fetchXcaliberToken();
  const status = xcaliberToken?.status?.toUpperCase() ?? '';

  if (status !== 'SUCCESS') return [];

  const queryURL = `${CONSTELLATION_SERVICES_LOCATION}/${xcaliberToken.tenantPath}/directory/api/v1/entities/provider/entries/query`;

  const response = await api.requestJSON<XCaliberAPI.ProviderEntity[]>(
    queryURL,
    {
      ...REQUEST_INIT_OPTIONS,
      headers: { Authorization: `Bearer ${xcaliberToken.token}` },
      method: HttpMethods.POST,
      body: JSON.stringify(providerQuery),
    }
  );

  return convertXcaliberEntitiesToCMX(
    response,
    isIPCServiceLine,
    isPathology
  );
};


const getProviderFromDirectoryV2XCaliber = async (
  lookupQuery: CMxCommonApp.LookupQuery,
  state: any
) => {
  const lookupText = (lookupQuery.text ?? '').trim();
  if (!lookupText.length) return [];

  const {
    providerRoleTypeConfig,
    providerType,
    invoice,
    shouldFilterActive,
    shouldFilterOutExternalProviders,
    shouldGreyOutExternalProviders,
    useProviderDirectoryLocations
  } = lookupQuery.context;

  const isBillingProviderSearch = providerType === ProviderType.BILLING_PROVIDER;
  const isPerformingProviderSearch = providerType === ProviderType.PERFORMING_PROVIDER;

  const roleTypeConfig = isPerformingProviderSearch ? providerRoleTypeConfig : [];

  const xcaliberToken = fetchXcaliberToken();
  const status = xcaliberToken?.status?.toUpperCase() ?? '';

  if (status !== 'SUCCESS') return [];

  const queryURL = `${CONSTELLATION_SERVICES_LOCATION}/${xcaliberToken.tenantPath}/directory/api/v1/entities/provider/entries/functions/search_billing_providers/execute`;

  let body: Record<string, any> = { provider_name: `%${lookupText}%` };

  if (isBillingProviderSearch) {
    body.service_date = invoice?.date_of_service;
    if (capabilityValue(useProviderDirectoryLocations?.on)) {
      body.location_name = invoice?.facility ?? '';
    }
  }

  const response = await api.requestJSON<CMxAPI.PageableList<XCaliberAPI.ProviderEntityIPC>>(
    queryURL,
    {
      ...REQUEST_INIT_OPTIONS,
      headers: { Authorization: `Bearer ${xcaliberToken.token}` },
      method: HttpMethods.POST,
      body: JSON.stringify(body),
    }
  );

  return convertXcaliberEntitiesToIPC(
    response?.content ?? [],
    isBillingProviderSearch,
    shouldFilterActive,
    shouldFilterOutExternalProviders,
    shouldGreyOutExternalProviders,
    providerType,
    roleTypeConfig
  );
};


const providerSearchDataAccess = createAsyncThunk<
  ProviderResults,
  CMxCommonApp.LookupQuery,
  { rejectValue: any }
>(InvoiceEditor.SEARCH_PROVIDERS_DATAACCESS, async (lookupQuery, { getState }) => {
  const {
    context: { searchType, meetsMinimum, processInstance, providerType }
  } = lookupQuery;

  const sequenceNumber = sequenceGenerator.next(searchType);
  const state = getState();

  if (!meetsMinimum) {
    return {
      providers: [],
      meta: {
        searchType,
        providerType,
        sequenceNumber
      }
    };
  }

  const isIPCServiceLine = appServiceLines.isIPC(processInstance?.serviceLine) ?? false;
  const isSurgery = appServiceLines.isSurgery(processInstance?.serviceLine) ?? false;
  const isPathology = appServiceLines.isPathology(processInstance?.serviceLine) ?? false;

  let providers: any[] = [];

  if (isIPCServiceLine || isSurgery) {
    providers = await getProviderFromDirectoryV2DataAccess(lookupQuery, state);
  } else {
    providers = await getProviderFromDirectoryDataAccess(
      lookupQuery,
      state,
      false,
      isPathology
    );
  }

  return {
    providers: invoiceUtils.providerOrder(providers),
    meta: {
      searchType,
      providerType,
      sequenceNumber
    }
  };
});

const getProviderFromDirectoryDataAccess = async (
  lookupQuery: CMxCommonApp.LookupQuery,
  state: any,
  isIPCServiceLine: boolean,
  isPathology: boolean
) => {
  const lookupText = (lookupQuery.text ?? '').trim();
  if (!lookupText.length) return [];

  const queryURL = '/dataaccess/read/v1';

  const providerSearchBody: CMxCommonApp.ProviderLookupRequest =
  lookupQuery.searchType === "id"
    ? {
        ...buildDataAccessRequestObject(state, "getProvider", "provider"),
        filterFields: [lookupText], // provider_id
      }
    : {
        ...buildDataAccessRequestObject(state, "queryForProvidersByName", "provider"),
        filterFields: [lookupText], // %name%
      };

  const response = await api.requestJSON<XCaliberAPI.ProviderEntity[]>(
    queryURL,
    {
      method: HttpMethods.POST,
      body: JSON.stringify(providerSearchBody),
    }
  );

  return convertXcaliberEntitiesToCMX(
    response,
    isIPCServiceLine,
    isPathology
  );
};

const getProviderFromDirectoryV2DataAccess = async (
  lookupQuery: CMxCommonApp.LookupQuery,
  state: any
) => {
  const lookupText = (lookupQuery.text ?? '').trim();
  if (!lookupText.length) return [];

  const {
    providerRoleTypeConfig,
    providerType,
    invoice,
    shouldFilterActive,
    shouldFilterOutExternalProviders,
    shouldGreyOutExternalProviders,
    useProviderDirectoryLocations
  } = lookupQuery.context;

  const isBillingProviderSearch = providerType === ProviderType.BILLING_PROVIDER;
  const isPerformingProviderSearch = providerType === ProviderType.PERFORMING_PROVIDER;

  const roleTypeConfig = isPerformingProviderSearch ? providerRoleTypeConfig : [];

  const queryURL = '/dataaccess/read/v1';
 const providerSearchBody: CMxCommonApp.ProviderSearchRequest = {
  ...buildDataAccessRequestObject(state, "searchForProvidersByName", "searchbillingprovider"),
  filterFieldsMap: {
    provider_name: `%${lookupText}%`
  }
};

  if (isBillingProviderSearch) {
    providerSearchBody.filterFieldsMap.service_date = invoice?.date_of_service;
    if (capabilityValue(useProviderDirectoryLocations?.on)) {
      providerSearchBody.filterFieldsMap.location_name = invoice?.facility ?? '';
    }
  }

  const response = await api.requestJSON<CMxAPI.PageableList<XCaliberAPI.ProviderEntityIPC>>(
    queryURL,
    {
      method: HttpMethods.POST,
      body: JSON.stringify(providerSearchBody),
    }
  );

  return convertXcaliberEntitiesToIPC(
    response?.content ?? [],
    isBillingProviderSearch,
    shouldFilterActive,
    shouldFilterOutExternalProviders,
    shouldGreyOutExternalProviders,
    providerType,
    roleTypeConfig
  );
};


const providerSearch = createAsyncThunk<
  ProviderResults,
  CMxCommonApp.LookupQuery,
  { rejectValue: any }
>(InvoiceEditor.SEARCH_PROVIDERS, async (lookupQuery, { dispatch, getState }) => {
  const state = getState();
  if (shouldFetchFromDataAccess(state)) {
    return dispatch(providerSearchDataAccess(lookupQuery)).unwrap();
  } else {
    return dispatch(providerSearchXCaliber(lookupQuery)).unwrap();
  }
});

// action for create a new invoice
const createInvoice = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  Accelerate.CreatebleInvoice,
  { rejectValue: any }
>(InvoiceEditor.CREATE_INVOICE, async (createbleInvoice, { dispatch, rejectWithValue }) => {
  const { invoice, processInstance } = createbleInvoice;

  // The invoice may originate from Redux state, which Redux Toolkit freezes
  // via Immer. Build a new payload instead of mutating the input.
  const invoicePayload = {
    ...invoice,
    transactions: [],
    billing_provider_type: invoice?.billing_provider_type ?? null,
    ordering_provider_type: invoice?.ordering_provider_type ?? null,
    performing_provider_type: invoice?.performing_provider_type ?? null,
    referring_provider_type: invoice?.referring_provider_type ?? null,
    billing_provider_credentials:
      invoice?.billing_provider_credentials || null,
    performing_provider_credentials:
      invoice?.performing_provider_credentials || null
  };

  try {
    // create a new invoice
    const newInvoice = await api.requestJSON<AmplifyAPI.Invoice>(
      `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/invoice/v1`,
      {
        ...REQUEST_INIT_OPTIONS,
        method: HttpMethods.POST,
        body: JSON.stringify(invoicePayload)
      }
    );

    // create an empty transaction with new invoices guid
    const addTxnURL = `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/transaction/v1?invoiceGuid=${newInvoice.guid}`;
    await api.requestJSON<AmplifyAPI.Invoice>(addTxnURL, {
      ...REQUEST_INIT_OPTIONS,
      method: HttpMethods.POST
    });

    // get data
    let updatedProcessInstance = await api.requestJSON<
      AmplifyAPI.ProcessInstance
    >(
      `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/v1?evidence=true&errors=true`,
      { ...REQUEST_INIT_OPTIONS }
    );

    dispatch(fetchActivity(updatedProcessInstance) as any);
    dispatch(fetchAllInvoices(updatedProcessInstance) as any);

    return updatedProcessInstance;
  } catch (error) {
    if (!checkInvoiceFieldErrors(error, dispatch)) {
      dispatch(
        amplifyFeedback({
          message: 'There were errors creating the invoice.',
          meta: FeedbackMeta.ERROR,
          dismissable: true
        })
      );
    }
    throw error;
  }
});

/**
 * Checks if there are any field errors in the given error object and dispatches
 * a feedback message with the list of fields that are not properly filled.
 *
 * @param {ErrorWithFieldErrors} error
 * @param {Function} dispatch
 * @returns {boolean}
 */
const checkInvoiceFieldErrors = (error, dispatch) => {
  const fieldsErrorsList = [];

  if (error?.errors && 'fieldErrors' in error?.errors) {
    // Check for errors in the 'feeSchedule' field.
    if (Object.keys(error?.errors?.fieldErrors).includes('feeSchedule')) {
      fieldsErrorsList.push('Billing Area');
    }

    // Check for errors in the 'departmentCode' field.
    if (Object.keys(error?.errors?.fieldErrors).includes('departmentCode')) {
      fieldsErrorsList.push('Department');
    }

    let errorMessage;
    switch (fieldsErrorsList.length) {
      case 0:
        errorMessage = 'Unknown field errors.';
        break;
      case 1:
        errorMessage = `The field '${fieldsErrorsList[0]}' is not properly filled.`;
        break;
      default:
        errorMessage = `These fields are not properly filled: ${fieldsErrorsList.join(
          ', '
        )}.`;
    }

    /**
     * The feedback message to dispatch.
     * @type {AmplifyFeedbackParams}
     */
    const feedbackMessage = {
      message: errorMessage,
      meta: FeedbackMeta.ERROR,
      dismissable: true
    };

    dispatch(amplifyFeedback(feedbackMessage));

    return true;
  }

  return false;
};

// action for get a invoice option
const getInvoiceOptions = createAsyncThunk<
  AmplifyAPI.InvoiceOptionsDTO,
  AmplifyAPI.ProcessInstance,
  { rejectValue: any }
>(InvoiceEditor.INVOICE_OPTIONS, async processInstance => {
  // create a new invoice
  const invoiceOptions = await api.requestJSON<AmplifyAPI.InvoiceOptionsDTO>(
    `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/invoice/v1`,
    {
      ...REQUEST_INIT_OPTIONS,
      method: HttpMethods.GET
    }
  );

  return invoiceOptions;
});

const chooseProvider = createAsyncThunk<void, CMxAPI.Provider, { rejectValue: any }>(
  InvoiceEditor.CHOOSE_PROVIDER,
  async (provider, { dispatch }) => {
    // leaving this as async for now even though it doesn't need to be.
    // in the future, it's expected that we'll do some sort of check to
    // make sure that the
    dispatch(addProvider(provider));
  }
);

const removeTransaction = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  CMxCommonApp.TransactionAction,
  { rejectValue: any }
>(InvoiceEditor.REMOVE_TRANSACTION, async (chargeSessionAction, { dispatch }) => {
  const serviceLine = chargeSessionAction.meta.processInstance.serviceLine;
  const processInstanceGuid =
    chargeSessionAction.meta.processInstance.guid.uuid;

  const deletedTxnURL = `/coding-api/processinstance/${serviceLine}/${processInstanceGuid}/transaction/v1?transactionGuid=${chargeSessionAction.meta.transaction.guid}`;
  const invoiceURL = `/coding-api/processinstance/${serviceLine}/${processInstanceGuid}/v1?evidence=true&errors=true`;

  // 1. delete transaction
  await api.request(deletedTxnURL, {
    ...REQUEST_INIT_OPTIONS,
    method: HttpMethods.DELETE
  });
  // 2. grab the full invoice
  let processInstance = await api.requestJSON<AmplifyAPI.ProcessInstance>(
    invoiceURL,
    { ...REQUEST_INIT_OPTIONS, method: HttpMethods.GET }
  );

  dispatch(fetchActivity(processInstance) as any);
  dispatch(fetchAllInvoices(processInstance) as any);
  return processInstance;
});

const saveInvoice = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  Accelerate.SavableInvoice,
  { rejectValue: any }
>(InvoiceEditor.SAVE_INVOICE, async (savableInvoice, { dispatch }) => {
  const { invoice, processInstance } = savableInvoice;

  // The invoice may originate from Redux state, which Redux Toolkit freezes
  // via Immer. Build a new payload instead of mutating the input.
  const invoicePayload = {
    ...invoice,
    billing_provider_type: invoice?.billing_provider_type ?? null,
    ordering_provider_type: invoice?.ordering_provider_type ?? null,
    performing_provider_type: invoice?.performing_provider_type ?? null,
    referring_provider_type: invoice?.referring_provider_type ?? null,
    billing_provider_credentials:
      invoice?.billing_provider_credentials || null,
    performing_provider_credentials:
      invoice?.performing_provider_credentials || null
  };

  try {
    await api.requestJSON<AmplifyAPI.Invoice>(
      `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/invoice/v1`,
      {
        ...REQUEST_INIT_OPTIONS,
        method: HttpMethods.PUT,
        body: JSON.stringify(invoicePayload)
      }
    );
  } catch (error) {
    if (!checkInvoiceFieldErrors(error, dispatch)) {
      dispatch(
        amplifyFeedback({
          message: 'There were errors saving changes.',
          meta: FeedbackMeta.ERROR,
          dismissable: true
        })
      );
    }
  } finally {
    const updatedProcessInstance: AmplifyAPI.ProcessInstance = await api.requestJSON<
      AmplifyAPI.ProcessInstance
    >(
      `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/v1?evidence=true&errors=true`,
      { ...REQUEST_INIT_OPTIONS }
    );
    dispatch(fetchActivity(updatedProcessInstance) as any);
    dispatch(fetchAllInvoices(updatedProcessInstance) as any);
    return updatedProcessInstance;
  }
});

export {
  addAdditionPerformer,
  addAdditionReferringPerformer,
  removeAdditionPerformer,
  removeAdditionReferringPerformer,
  chooseAdditionPerformer,
  chooseAdditionReferringPerformer,
  createInvoice,
  changePerformDate,
  chooseProvider,
  billingAreaOptions,
  removeTransaction,
  cancelChargeEdit,
  providerSearch,
  removeProvider,
  addProvider,
  saveInvoice,
  getInvoiceOptions,
  feeScheduleChange,
  departmentChange
};
